import { Download, Scale, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../../components/ui/Drawer';
import { Badge } from '../../../components/ui/Badge';
import { fmtAmount } from '../utils';
import { deriveLifecycle, LifecycleStepper } from './LifecycleStepper';

export function InvoiceDrawer({ row, onClose, locale }) {
  const { t } = useTranslation();
  if (!row) return null;

  const steps      = deriveLifecycle(row.statusGroup, t);
  const amountHT   = row.amountTTC != null && row.vatAmount != null
    ? row.amountTTC - row.vatAmount
    : (row.amountEur ?? null);
  const canPay     = row.statusGroup !== 'paid' && row.statusGroup !== 'cancelled';
  const isDisputed = row.statusGroup === 'disputed';
  const statusLabel = row.statusKey ? t(`invoices.${row.statusKey}`) : '—';

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      title={row.reference}
      subtitle={[row.shipToName, row.shipToCode].filter(Boolean).join(' · ')}
      headerRight={<Badge variant={row.statusVariant}>{statusLabel}</Badge>}
      width="w-[640px]"
      footer={
        <>
          {canPay && (
            <button className="flex items-center gap-1.5 text-rose-600 text-[12.5px] font-medium hover:text-rose-700 transition">
              <Scale size={13} strokeWidth={1.75} />
              {t('invoices.btn_dispute')}
            </button>
          )}
          <div className="flex-1" />
          {row.pdfUrl ? (
            <a
              href={row.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="h-9 px-4 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              <Download size={13} strokeWidth={1.75} />
              {t('invoices.btn_pdf')}
            </a>
          ) : (
            <button
              disabled
              className="h-9 px-4 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12.5px] font-medium text-slate-400 cursor-not-allowed opacity-60"
            >
              <Download size={13} strokeWidth={1.75} />
              {t('invoices.btn_pdf')}
            </button>
          )}
          {canPay && (
            <button className="h-9 px-4 flex items-center gap-1.5 rounded-lg bg-primary text-white text-[12.5px] font-semibold hover:opacity-90 transition">
              <CreditCard size={13} strokeWidth={1.75} />
              {t('invoices.btn_pay')} {fmtAmount(row.amountTTC ?? row.amountEur, row.currency, locale)}
            </button>
          )}
        </>
      }
    >
      {isDisputed && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3.5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Scale size={15} strokeWidth={1.75} className="text-violet-600" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-violet-800">{t('invoices.disputed_banner_title')}</p>
            <p className="text-[12px] text-violet-600 mt-0.5">
              {row.disputeNote || t('invoices.disputed_banner_body')}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="w-14 h-9 mb-3 overflow-hidden">
              <img src="/mfb-logo.png" alt="MFB" className="w-full h-full object-contain" />
            </div>
            <p className="font-bold text-[13px] text-slate-900">Maroc Fruit Board — Fresh Logistics</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">{t('invoices.company_address')}</p>
            <p className="text-[11.5px] text-slate-500">{t('invoices.company_siren')}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
              {t('invoices.header_invoice')}
            </p>
            <p className="text-[22px] font-bold text-slate-900 leading-tight">{row.reference}</p>
            <p className="text-[12px] text-slate-500 mt-1">{t('invoices.drawer_issued')} {row.issuedAt}</p>
            <p className="text-[12px] text-slate-500">{t('invoices.drawer_due')} {row.dueDate}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-100">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              {t('invoices.drawer_billed_to')}
            </p>
            <p className="font-bold text-[13px] text-slate-900">{row.shipToName || '—'}</p>
            {row.billToCustomerNo && (
              <p className="text-[11.5px] text-slate-500 mt-0.5">{row.billToCustomerNo}</p>
            )}
            {row.finalDestination && (
              <p className="text-[11.5px] text-slate-500">{row.finalDestination}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              {t('invoices.drawer_shipment_ref')}
            </p>
            <p className="font-bold font-mono text-[13px] text-slate-900">{row.shipToCode || '—'}</p>
          </div>
        </div>

        {row.lineItems?.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left pb-2 font-semibold">{t('invoices.drawer_designation')}</th>
                  <th className="text-right pb-2 font-semibold">{t('invoices.drawer_qty')}</th>
                  <th className="text-right pb-2 font-semibold">{t('invoices.drawer_unit_price')}</th>
                  <th className="text-right pb-2 font-semibold">{t('invoices.drawer_total_ht')}</th>
                </tr>
              </thead>
              <tbody>
                {row.lineItems.map((item, i) => (
                  <tr key={i} className="border-b border-slate-50 last:border-0">
                    <td className="py-2.5 pr-4">
                      <p className="font-medium text-slate-800">
                        {item.description || item.designation || item.label || '—'}
                      </p>
                      {item.subtitle && (
                        <p className="text-[11px] text-slate-400 mt-0.5">{item.subtitle}</p>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-slate-600 py-2.5 px-2">
                      {item.qty ?? item.quantity ?? 1}
                    </td>
                    <td className="text-right tabular-nums text-slate-600 py-2.5 px-2">
                      {fmtAmount(item.unitPrice ?? item.pu ?? item.price, row.currency, locale)}
                    </td>
                    <td className="text-right tabular-nums font-semibold text-slate-800 py-2.5 pl-2">
                      {fmtAmount(item.totalHT ?? item.total ?? item.amount, row.currency, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border-t border-slate-200 pt-3 space-y-1.5">
          <div className="flex justify-end gap-16 text-[12.5px] text-slate-500">
            <span>{t('invoices.drawer_total_label_ht')}</span>
            <span className="tabular-nums">{fmtAmount(amountHT, row.currency, locale)}</span>
          </div>
          <div className="flex justify-end gap-16 text-[12.5px] text-slate-500">
            <span>{row.vatAmount ? t('invoices.drawer_vat') : t('invoices.drawer_vat_export')}</span>
            <span className="tabular-nums">
              {row.vatAmount ? fmtAmount(row.vatAmount, row.currency, locale) : '—'}
            </span>
          </div>
          <div className="flex justify-end gap-16 text-[14px] font-bold text-slate-900 pt-2 border-t border-slate-100">
            <span>{t('invoices.drawer_total_ttc')}</span>
            <span className="tabular-nums">
              {fmtAmount(row.amountTTC ?? row.amountEur, row.currency, locale)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-[13px] font-semibold text-slate-800 mb-5">{t('invoices.drawer_lifecycle')}</p>
        <LifecycleStepper steps={steps} />
      </div>
    </Drawer>
  );
}
